import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}
const termsContent = `
Última actualización: marzo 2026

1. ACEPTACIÓN DE LOS TÉRMINOS
Al adquirir y/o utilizar el producto digital "Protocolo Cortisol-Zero™" (en adelante, "el Protocolo"), usted acepta estos Términos y Condiciones en su totalidad. Si no está de acuerdo con alguno de estos términos, no debe adquirir ni utilizar el Protocolo.

2. DESCRIPCIÓN DEL PRODUCTO
El Protocolo Cortisol-Zero™ es un producto digital en formato PDF que contiene información educativa sobre técnicas de termorregulación, cronobiología y gestión del sueño. El contenido es de carácter informativo y educativo.

3. LICENCIA DE USO
Al completar su compra, se le otorga una licencia personal, intransferible y no exclusiva para acceder y utilizar el contenido del Protocolo. Queda prohibida la reproducción, distribución, modificación o reventa total o parcial del contenido sin autorización expresa por escrito.

4. DESCARGO DE RESPONSABILIDAD MÉDICA
El Protocolo Cortisol-Zero™ NO constituye consejo médico, diagnóstico ni tratamiento. La información proporcionada es de carácter educativo y no sustituye la consulta con un profesional de la salud calificado. Antes de implementar cualquier cambio en sus hábitos de sueño o salud, consulte con su médico. Los resultados individuales pueden variar significativamente.

5. POLÍTICA DE REEMBOLSO
Ofrecemos una garantía de satisfacción. Si después de aplicar el protocolo durante al menos una noche no percibe mejora alguna, puede solicitar un reembolso completo enviando un correo electrónico dentro de los 30 días posteriores a la compra. El reembolso se procesará en un plazo de 5 a 10 días hábiles.

6. PROPIEDAD INTELECTUAL
Todo el contenido del Protocolo Cortisol-Zero™, incluyendo textos, gráficos, metodologías y marca, está protegido por las leyes de propiedad intelectual. El uso no autorizado puede resultar en acciones legales.

7. LIMITACIÓN DE RESPONSABILIDAD
En ningún caso seremos responsables por daños directos, indirectos, incidentales o consecuentes derivados del uso o la imposibilidad de uso del Protocolo. El usuario asume toda la responsabilidad por la aplicación de las técnicas descritas.

8. MODIFICACIONES
Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones entrarán en vigor desde su publicación. El uso continuado del producto después de cualquier modificación constituye la aceptación de los nuevos términos.

9. LEY APLICABLE
Estos términos se regirán e interpretarán de acuerdo con las leyes aplicables en la jurisdicción correspondiente.
`.trim();
const privacyContent = `
Última actualización: marzo 2026

1. INFORMACIÓN QUE RECOPILAMOS
Para procesar su compra, recopilamos únicamente la información necesaria para completar la transacción:
• Dirección de correo electrónico (para envío del producto y comunicación sobre la compra)
• Información de pago (procesada de forma segura por nuestro proveedor de pagos externo)

No recopilamos datos sensibles de salud, información biométrica ni datos de ubicación.

2. USO DE LA INFORMACIÓN
La información recopilada se utiliza exclusivamente para:
• Procesar y entregar su compra
• Enviar el producto digital adquirido
• Gestionar solicitudes de reembolso
• Cumplir con obligaciones legales aplicables

No utilizamos su información para elaborar perfiles, tomar decisiones automatizadas ni para fines distintos a los aquí descritos.

3. COMPARTICIÓN DE DATOS
No vendemos, alquilamos ni compartimos su información personal con terceros con fines comerciales. Su información solo puede ser compartida con:
• Proveedores de servicios de pago (para procesar la transacción de forma segura)
• Autoridades competentes (cuando sea requerido por ley)

4. SEGURIDAD DE LOS DATOS
Implementamos medidas de seguridad técnicas y organizativas razonables para proteger su información personal contra acceso no autorizado, pérdida o alteración. Las transacciones de pago son procesadas mediante protocolos de encriptación estándar de la industria.

5. RETENCIÓN DE DATOS
Conservamos su información personal únicamente durante el tiempo necesario para cumplir con los fines descritos en esta política y para cumplir con nuestras obligaciones legales. Los datos de transacción se conservan según los plazos exigidos por la legislación fiscal aplicable.

6. SUS DERECHOS
Usted tiene derecho a:
• Acceder a los datos personales que tenemos sobre usted
• Solicitar la rectificación de datos inexactos
• Solicitar la eliminación de sus datos personales
• Oponerse al procesamiento de sus datos
• Solicitar la portabilidad de sus datos

Para ejercer cualquiera de estos derechos, puede enviar una solicitud por correo electrónico.

7. COOKIES Y TECNOLOGÍAS DE SEGUIMIENTO
Nuestra página de venta puede utilizar cookies esenciales para el funcionamiento del sitio y cookies analíticas para comprender el rendimiento de la página. No utilizamos cookies de publicidad ni de seguimiento de terceros con fines de marketing.

8. MENORES DE EDAD
Este producto no está dirigido a menores de 18 años. No recopilamos intencionalmente información de menores de edad.

9. CAMBIOS EN ESTA POLÍTICA
Nos reservamos el derecho de actualizar esta Política de Privacidad. Cualquier cambio será publicado en esta página con la fecha de actualización correspondiente.
`.trim();
export function LegalModal({
  isOpen,
  onClose,
  type
}: LegalModalProps) {
  const title = type === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad';
  const content = type === 'terms' ? termsContent : privacyContent;
  return <AnimatePresence data-id="element-53">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-54">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-55" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative z-10 w-full max-w-2xl max-h-[80vh] bg-[#0f172a] border border-slate-700/50 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-56">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-700/50 shrink-0" data-id="element-57">
              <h2 className="font-heading text-xl text-white" data-id="element-58">{title}</h2>
              <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-700/50" data-id="element-59">
                <XIcon size={20} data-id="element-60" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6 overflow-y-auto flex-1 custom-scrollbar" data-id="element-61">
              <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-line font-body" data-id="element-62">
                {content}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-700/50 shrink-0" data-id="element-63">
              <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-slate-700/50 text-slate-300 text-sm font-medium hover:bg-slate-700 transition-colors" data-id="element-64">
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}